import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { DeviceRow } from 'components/tables/data/device/types';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ADD_FORM_PAGE_PATH, ADD_REVISION_PAGE_PATH } from 'routes/paths';
import Button from 'components/Button';

interface BulkCreateRevisionActionProps {
  devices: DeviceRow[];
  kind: 'revision' | 'form';
}

/**
 * Creates a new revision/form from selected devices.
 */
export default function BulkCreateRevisionAction({ devices, kind }: BulkCreateRevisionActionProps) {
  const { getAction } = useApi();

  const [hasAssignPermission, setHasAssignPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const permissionSlug = kind === 'revision' ? 'assign-revisions' : 'assign-forms';
  const targetPath = kind === 'revision' ? ADD_REVISION_PAGE_PATH : ADD_FORM_PAGE_PATH;
  const buttonLabel = kind === 'revision' ? 'Vytvoriť revíznu správu' : 'Vytvoriť formulár';

  const selectedDepartmentId = useMemo(() => {
    if (devices.length === 0) {
      return undefined;
    }

    const departmentIds = new Set(devices.map(({ department }) => department.departmentId));

    return departmentIds.size === 1 ? devices[0].department.departmentId : undefined;
  }, [devices]);

  const selectedDeviceTypeId = useMemo(() => {
    if (devices.length === 0) {
      return undefined;
    }

    const deviceTypeIds = new Set(devices.map(({ deviceType }) => deviceType.deviceTypeId));

    return deviceTypeIds.size === 1 ? devices[0].deviceType.deviceTypeId : undefined;
  }, [devices]);

  const selectedOrganizationId = useMemo(() => {
    if (devices.length === 0) {
      return undefined;
    }

    return devices[0].organization.organizationId;
  }, [devices]);

  const selectedStructure = useMemo(() => {
    if (devices.length === 0) {
      return undefined;
    }

    const structures = new Set(devices.map(({ structure }) => structure));

    return structures.size === 1 ? devices[0].structure : undefined;
  }, [devices]);

  const hasSameParent = useMemo(() => {
    if (devices.length === 0) {
      return false;
    }

    const parentIds = new Set(devices.map(({ parentDevice }) => parentDevice?.deviceId ?? null));

    return parentIds.size === 1;
  }, [devices]);

  useEffect(() => {
    if (!selectedDepartmentId) {
      setHasAssignPermission(false);
      setPermissionLoading(false);
      return;
    }

    const action = getAction('AuthUserHasPermissionInDepartment');
    let cancelled = false;

    setPermissionLoading(true);

    action({
      parameters: {
        departmentId: String(selectedDepartmentId),
        permissionSlug,
      },
    })
      .then(({ hasPermission }) => {
        if (!cancelled) {
          setHasAssignPermission(hasPermission);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setHasAssignPermission(false);
        }

        panic(error);
      })
      .finally(() => {
        if (!cancelled) {
          setPermissionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getAction, permissionSlug, selectedDepartmentId]);

  const addPageLink = useMemo(() => {
    if (
      !selectedDepartmentId ||
      !selectedDeviceTypeId ||
      !selectedOrganizationId ||
      !selectedStructure ||
      devices.length === 0
    ) {
      return '';
    }

    const revisionName = devices.map(({ deviceName }) => deviceName).join(', ');
    const initialDevices = devices.map(({ deviceId }) => ({ deviceId }));
    const devicesParam = encodeURIComponent(JSON.stringify(initialDevices));

    const query = [
      `revisionName=${encodeURIComponent(revisionName)}`,
      `organizationId=${selectedOrganizationId}`,
      `departmentId=${selectedDepartmentId}`,
      `deviceTypeId=${selectedDeviceTypeId}`,
      `devices=${devicesParam}`,
    ].join('&');

    return `${targetPath.original}?${query}`;
  }, [devices, selectedDepartmentId, selectedDeviceTypeId, selectedOrganizationId, selectedStructure, targetPath]);

  const canCreate =
    !permissionLoading &&
    Boolean(selectedDepartmentId) &&
    Boolean(selectedDeviceTypeId) &&
    Boolean(selectedStructure) &&
    hasSameParent &&
    hasAssignPermission &&
    Boolean(addPageLink);

  if (!canCreate) {
    return null;
  }

  return (
    <Button component={Link} variant="primary" size="sm" leftIcon={<IconPlus />} to={addPageLink}>
      {buttonLabel} ({devices.length})
    </Button>
  );
}
